import React from "react"
import { useState, useEffect } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import moment from "moment"
import "moment-timezone"
import "react-phone-number-input/style.css"

import { useData } from "../../contexts/DataContext"
import Loader from "../../components/Loader/index.js"

import styles from "./index.module.css"


const FlightsForm = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const { inventory, airports, flights, loading: dataLoading } = useData()
    const [outboundFlight, setOutboundFlight] = useState(null)
    const [inboundFlight, setInboundFlight] = useState(null)
    const [outboundInventory, setOutboundInventory] = useState([])
    const [inboundInventory, setInboundInventory] = useState([])

    const queryParams = new URLSearchParams(location.search)
    const formData = {
        departure: queryParams.get("departure"),
        arrival: queryParams.get("arrival"),
        promoCode: queryParams.get("promoCode"),
        passengers: parseInt(queryParams.get("passengerCount"), 10),
        departureDate: moment.unix(parseInt(queryParams.get("departureDate"), 10)).format("YYYY-MM-DD"),
        returnDate: queryParams.get("returnDate") ? moment.unix(parseInt(queryParams.get("returnDate"), 10)).format("YYYY-MM-DD") : null,
    }

    const handleContinue = () => {
        let bookingData = {
            passengerCount: formData.passengers,
            promoCode: formData.promoCode,
            outboundFlight: outboundFlight,
        }

        if (formData.returnDate) {
            bookingData["inboundFlight"] = inboundFlight
        }

        navigate(`/booking-form`, {
            state: { bookingData }
        })
    }

    useEffect(() => {
        if (dataLoading) return

        const processFlights = (inventory) => {
            return inventory.map((ticket) => {
                let departure_timezone = airports.find((item) => item.code === ticket.departure)?.timezone
                let arrival_timezone = airports.find((item) => item.code === ticket.arrival)?.timezone
                let airlineLogo = flights.find(flight => flight.flightNo === ticket.flightNo)?.airlineLogo

                let departureDateTime = moment(`${ticket.departureDate} ${ticket.departureTime}`, "YYYY-MM-DD HH:mm").tz(departure_timezone, true)
                let arrivalDateTime = moment(`${ticket.departureDate} ${ticket.arrivalTime.split("(+1)")[0]}`, "YYYY-MM-DD HH:mm")

                arrivalDateTime = ticket.arrivalTime.includes("(+1)")
                    ? arrivalDateTime.tz(arrival_timezone, true).add(1, "day")
                    : arrivalDateTime.tz(arrival_timezone, true)
                let duration = moment.duration(arrivalDateTime.diff(departureDateTime))
                return {
                    ...ticket,
                    departureDateTime,
                    arrivalDateTime,
                    duration: `${duration.hours()} hrs ${duration.minutes()} mins`,
                    airlineLogo,
                }
            })
        }

        let outboundFlightNo = flights.filter(flight => flight.departure === formData.departure && flight.arrival === formData.arrival).map(flight => flight.flightNo)
        let outboundInventory = inventory.filter(item => outboundFlightNo.includes(item.flightNo) && item.departureDate === formData.departureDate)
        outboundInventory = outboundInventory ? processFlights(outboundInventory) : []
        setOutboundInventory(outboundInventory)

        // Only process return flight if it exists and we have inboundFlight ID
        if (formData.returnDate) {
            let inboundFlightNo = flights.filter(flight => flight.departure === formData.arrival && flight.arrival === formData.departure).map(flight => flight.flightNo)
            let inboundInventory = inventory.filter(item => inboundFlightNo.includes(item.flightNo) && item.departureDate === formData.returnDate)
            inboundInventory = inboundInventory ? processFlights(inboundInventory) : []
            setInboundInventory(inboundInventory)
        }

    }, [formData.departure, formData.departureDate, formData.returnDate, formData.arrival, inventory, airports, flights, dataLoading])

    return (
        <section className="py-4">
            <div className="row my-3">
                <div className="col">
                    <div className={`form-group ${styles.ricoFormGroup}`}>
                        <label className={`form-label ${styles.label}`}>
                            From
                            <span className={`material-symbols-outlined ${styles.labelIcon}`}>
                                flight_takeoff
                            </span>
                        </label>
                        <input type="text" className="form-control" value={airports.find((item) => item.code === formData?.departure)?.label || ""} disabled />
                    </div>
                </div>
                <div className="col">
                    <div className={`form-group ${styles.ricoFormGroup}`}>
                        <label className={`form-label ${styles.label}`}>
                            To
                            <span className={`material-symbols-outlined ${styles.labelIcon}`}>
                                flight_land
                            </span>
                        </label>
                        <input type="text" className="form-control" value={airports.find((item) => item.code === formData?.arrival)?.label || ""} disabled />
                    </div>
                </div>
                <div className="col">
                    <div className={`form-group ${styles.ricoFormGroup}`}>
                        <label htmlFor="departureDateInput" className={`form-label ${styles.label}`}>
                            Departure Date
                            <span className={`material-symbols-outlined ${styles.labelIcon}`}>
                                calendar_month
                            </span>
                        </label>
                        <input type="text" className="form-control" value={formData?.departureDate} disabled />
                    </div>
                </div>
                {formData.returnDate &&
                    <div className="col">
                        <div className={`form-group ${styles.ricoFormGroup}`}>
                            <label htmlFor="returnDateInput" className={`form-label ${styles.label}`}>
                                Return Date
                                <span className={`material-symbols-outlined ${styles.labelIcon}`}>
                                    calendar_month
                                </span>
                            </label>
                            <input type="text" className="form-control" value={formData.returnDate} disabled />
                        </div>
                    </div>
                }
                <div className="col">
                    <div className={`form-group ${styles.ricoFormGroup}`}>
                        <label htmlFor="passengerCountInput" className={`form-label ${styles.label}`}>
                            No of Seats
                            <span className={`material-symbols-outlined ${styles.labelIcon}`}>
                                airline_seat_recline_extra
                            </span>
                        </label>
                        <input type="text" className="form-control" value={formData.passengers} disabled />
                    </div>
                </div>
            </div>
            {dataLoading ? <Loader /> : <>
                <div className="row my-3">
                    <div className="col">
                        <div className="d-flex justify-content-between align-items-center mt-5">
                            <h4 className="heading-text-primary">Outbound Flights</h4>
                        </div>
                        {outboundInventory && outboundInventory.length > 0 ? outboundInventory.map((flight) =>
                            <div className="row">
                                <div className={`col`} key={`flight-${flight.id}`}>
                                    <section className={`${styles.flightDetailWrapper} ${outboundFlight === flight.id ? styles.activeFlightDetailWrapper : ""}`} onClick={() => setOutboundFlight(flight.id === outboundFlight ? null : flight.id)}>
                                        <div className={styles.flightDetailBody}>
                                            <span className={styles.flightDepartureWrapper}>
                                                <p className={styles.flightDate}>
                                                    {moment(flight.departureDateTime).format("ddd DD MMM")}
                                                </p>
                                                <p className={styles.flightTime}>
                                                    {flight.departureTime}
                                                </p>
                                                <p className={styles.flightLocCode}>
                                                    {flight.departure}
                                                </p>
                                                <p className={styles.flightLoc}>
                                                    {airports.find((item) => item.code === flight.departure)?.label || ""}
                                                </p>
                                            </span>
                                            <span className={styles.flightDurationWrapper}>
                                                <span className={styles.duration}>{flight.duration}</span>
                                                <div className={styles.line}>
                                                    <span className={styles.arrow}></span>
                                                </div>
                                                <img
                                                    className="mt-2"
                                                    src={flight.airlineLogo}
                                                    alt={`${flight.flightNo} logo`}
                                                    width={"100px"}
                                                />
                                            </span>
                                            <span className={styles.flightArrivalWrapper}>
                                                <p className={styles.flightDate}>
                                                    {moment(flight.arrivalDateTime).format("ddd DD MMM")}
                                                </p>
                                                <p className={styles.flightTime}>
                                                    {flight.arrivalTime.split("(+1)")[0]}
                                                </p>
                                                <p className={styles.flightLocCode}>
                                                    {flight.arrival}
                                                </p>
                                                <p className={styles.flightLoc}>
                                                    {airports.find((item) => item.code === flight.arrival)?.label || ""}
                                                </p>
                                            </span>
                                        </div>
                                        <div className={styles.flightDetailFooter}>
                                            <span className={styles.flightNo}>
                                                <span className={`material-symbols-outlined ${styles.flightNoIcon}`}>
                                                    flight
                                                </span>
                                                <span className={styles.flightPlane}>A330</span>
                                                {flight.flightNo}
                                            </span>
                                            <span className={styles.flightPrice}>${flight.price}</span>
                                        </div>
                                    </section>
                                </div>
                            </div>) :
                            <div className="col">
                                <p>No Outbound Flights Available</p>
                            </div>
                        }
                    </div>
                    {formData.returnDate && <div className="col">
                        <div className="d-flex justify-content-between align-items-center mt-5">
                            <h4 className="heading-text-primary">Inbound Flights</h4>
                        </div>
                        {inboundInventory && inboundInventory.length > 0 ? inboundInventory.map((flight) =>
                            <div className="row">
                                <div className={`col`} key={`flight-${flight.id}`}>
                                    <section className={`${styles.flightDetailWrapper} ${inboundFlight === flight.id ? styles.activeFlightDetailWrapper : ""}`} onClick={() => setInboundFlight(flight.id === inboundFlight ? null : flight.id)}>
                                        <div className={styles.flightDetailBody}>
                                            <span className={styles.flightDepartureWrapper}>
                                                <p className={styles.flightDate}>
                                                    {moment(flight.departureDateTime).format("ddd DD MMM")}
                                                </p>
                                                <p className={styles.flightTime}>
                                                    {flight.departureTime}
                                                </p>
                                                <p className={styles.flightLocCode}>
                                                    {flight.departure}
                                                </p>
                                                <p className={styles.flightLoc}>
                                                    {airports.find((item) => item.code === flight.departure)?.label || ""}
                                                </p>
                                            </span>
                                            <span className={styles.flightDurationWrapper}>
                                                <span className={styles.duration}>{flight.duration}</span>
                                                <div className={styles.line}>
                                                    <span className={styles.arrow}></span>
                                                </div>
                                                <img
                                                    className="mt-2"
                                                    src={flight.airlineLogo}
                                                    alt={`${flight.flightNo} logo`}
                                                    width={"100px"}
                                                />
                                            </span>
                                            <span className={styles.flightArrivalWrapper}>
                                                <p className={styles.flightDate}>
                                                    {moment(flight.arrivalDateTime).format("ddd DD MMM")}
                                                </p>
                                                <p className={styles.flightTime}>
                                                    {flight.arrivalTime.split("(+1)")[0]}
                                                </p>
                                                <p className={styles.flightLocCode}>
                                                    {flight.arrival}
                                                </p>
                                                <p className={styles.flightLoc}>
                                                    {airports.find((item) => item.code === flight.arrival)?.label || ""}
                                                </p>
                                            </span>
                                        </div>
                                        <div className={styles.flightDetailFooter}>
                                            <span className={styles.flightNo}>
                                                <span className={`material-symbols-outlined ${styles.flightNoIcon}`}>
                                                    flight
                                                </span>
                                                <span className={styles.flightPlane}>A330</span>
                                                {flight.flightNo}
                                            </span>
                                            <span className={styles.flightPrice}>${flight.price}</span>
                                        </div>
                                    </section>
                                </div>
                            </div>) :
                            <div className="col">
                                <p>No Inbound Flights Available</p>
                            </div>
                        }
                    </div>}
                </div>
            </>}

            <div className={styles.buttonContainer}>
                <button className="btn-primary" disabled={!outboundFlight || (!inboundFlight && formData.returnDate)} onClick={handleContinue}>Continue</button>
            </div>
        </section>
    )
}

export default FlightsForm